import React, { useCallback, useEffect, useState } from 'react';

import dynamic from 'next/dynamic';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';

import type { GetStaticPaths, GetStaticProps, NextPage } from 'next';

import { useMediaQuery } from '@mui/material';

import { useQuery } from 'react-query';

import { useWindowSize } from '@hooks_common/useWindowSize';

import { getCompaniesList } from '@hooks_iqfu/public/useCompanyData';
import { getCityGroups, getSalesStats } from '@hooks_iqfu/public/useMarketData';
import { getSaleCasesList } from '@hooks_iqfu/public/useSaleCase';

import { validatePrefectureCode } from '@utils_iqfu/hasValidPrefectureCode';

import BreadCrumbs from '@components_iqfu/public/bread_crumbs/BreadCrumbs';
import LandHouseCard from '@components_iqfu/public/check_price/landhouse_card/LandHouseCard';
import SimulatorCard from '@components_iqfu/public/check_price/simulator_card/SimulatorCard';
import CitiesLinkList from '@components_iqfu/public/cities_link_list/CitiesLinkList';
import CompanyCard from '@components_iqfu/public/company_card/CompanyCard';
import PrimaryCTA from '@components_iqfu/public/cta/PrimaryCTA';
import FloatingCV from '@components_iqfu/public/floating_cv/floating_cv';
import Footer from '@components_iqfu/public/footer/Footer';
import PageMainLead from '@components_iqfu/public/page_main_lead/PageMainLead';
import PrefecturesLinkList from '@components_iqfu/public/prefectures_list/PrefecturesLinkList';
import LandHouseGeneralInfo from '@components_iqfu/public/price/landhouse_general_info/LandHouseGeneralInfo';
import PricePageMarketInfo from '@components_iqfu/public/price_page_market_info/PricePageMarketInfo';
import PublicPageContainer from '@components_iqfu/public/public_page_container/PublicPageContainer';
import RenderOnScroll from '@components_iqfu/public/render_on_scroll/RenderOnScroll';
import SaleCaseArea from '@components_iqfu/public/sale_case_area/SaleCaseArea';
import SalesStatsSurveys from '@components_iqfu/public/sale_stats_surveys/SaleStatsSurveys';
import SearchCompaniesStepper from '@components_iqfu/public/search_companies_stepper/SearchCompaniesStepper';
import SearchCompaniesWrapper from '@components_iqfu/public/search_companies_wrapper/SearchCompaniesWrapper';
import SectionLead from '@components_iqfu/public/section_lead/SectionLead';
import SectionLeadLeftAlign from '@components_iqfu/public/section_lead_left_align/SectionLeadLeftAlign';
import TrendDescription from '@components_iqfu/public/trend_description/TrendDescription';
import WantToKnowRanking from '@components_iqfu/public/want_to_know_ranking/WantToKnowRanking';
const SaleCasesMap = dynamic(() => import('@components_iqfu/public/mapbox/SaleCasesMap'), {
  ssr: false,
});

import { CompanyListItem } from '@type-def_iqfu/public/CompanyListItem';
import { CityGroups, SalesStatsData } from '@type-def_iqfu/public/MarketData';
import { SaleCaseItem } from '@type-def_iqfu/public/SaleCaseItem';

import { MediaQuery, zoomRate } from '@constants_common';
import { ISRRevalidateTime, PrefecturalOfficeCoordinate, PublicPrefecturesWithAll } from '@constants_iqfu';
import styles from '@styles_iqfu/public/price.module.scss';
import { PrerenderPrefecturesCodeList } from 'src/iqfu/data/PrerenderPagesData';

const pageLabel = 'price-landhouses-prefectures';

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const prefectureCode = params?.prefecture_code;

  const isValidUrl = validatePrefectureCode(String(prefectureCode));

  const [landSaleCaseList, houseSaleCaseList, serverCachedCompaniesData, landSalesStats, houseSalesStats, cityGroups] =
    isValidUrl
      ? await Promise.all([
          getSaleCasesList({
            prefecture_code: Number(prefectureCode),
            property_type: 'land',
            limit: 50,
          }),
          getSaleCasesList({
            prefecture_code: Number(prefectureCode),
            property_type: 'used_house,new_house',
            limit: 50,
          }),
          getCompaniesList({
            prefecture_code: Number(prefectureCode),
            sort: 'sale_case_land_house',
            limit: 3,
          }),
          getSalesStats({
            property_type: 'land',
            prefecture_code: Number(prefectureCode),
          }),
          getSalesStats({
            property_type: 'house',
            prefecture_code: Number(prefectureCode),
          }),
          getCityGroups(Number(prefectureCode)),
        ])
      : [null, null, null, null, null, null];

  const prefectureLatLong = {
    lat: PrefecturalOfficeCoordinate[Number(prefectureCode)]?.[0] || 0,
    lng: PrefecturalOfficeCoordinate[Number(prefectureCode)]?.[1] || 0,
  };

  return {
    props: {
      prefectureCode,
      isValidUrl,
      serverCachedHouseSaleCasesData: houseSaleCaseList?.sale_cases || [],
      serverCachedLandSaleCasesData: landSaleCaseList?.sale_cases || [],
      serverCachedCompaniesData: serverCachedCompaniesData?.companies || [],
      prefectureLatLong,
      landSalesStats,
      houseSalesStats,
      cityGroups,
    },
    revalidate: ISRRevalidateTime,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const PageParams = PrerenderPrefecturesCodeList.map((prefecture_code) => {
    return {
      params: {
        prefecture_code: String(prefecture_code),
      },
    };
  });

  return {
    paths: PageParams,
    fallback: 'blocking',
  };
};

type Props = {
  prefectureCode: string;
  isValidUrl: boolean;
  serverCachedHouseSaleCasesData: SaleCaseItem[];
  serverCachedLandSaleCasesData: SaleCaseItem[];
  serverCachedCompaniesData: CompanyListItem[];
  prefectureLatLong: { lat: number; lng: number };
  landSalesStats: SalesStatsData | null;
  houseSalesStats: SalesStatsData | null;
  cityGroups: CityGroups | null;
};

const LandHousesPrefecture: NextPage<Props> = ({
  prefectureCode,
  isValidUrl,
  serverCachedHouseSaleCasesData,
  serverCachedLandSaleCasesData,
  serverCachedCompaniesData,
  prefectureLatLong,
  landSalesStats,
  houseSalesStats,
  cityGroups,
}) => {
  const router = useRouter();

  const displayPrefectureName = PublicPrefecturesWithAll[Number(prefectureCode)];
  const isMobile = useMediaQuery(MediaQuery.lg);
  const { height: windowHeight } = useWindowSize();

  const { data: companiesData } = useQuery(
    ['LandHousesPrefectureCompaniesList', prefectureCode],
    async () => {
      const result = await getCompaniesList({
        prefecture_code: Number(prefectureCode),
        sort: 'sale_case_land_house',
        limit: 3,
      });
      return result.companies || [];
    },
    { enabled: isValidUrl, cacheTime: 1000 * 60 * 5, initialData: serverCachedCompaniesData },
  );

  const { data: landSaleCasesData } = useQuery(
    ['LandPrefectureSaleCases', prefectureCode],
    async () => {
      const result = await getSaleCasesList({
        prefecture_code: Number(prefectureCode),
        property_type: 'land',
        limit: 50,
      });
      return result.sale_cases || [];
    },
    {
      enabled: isValidUrl,
      cacheTime: 1000 * 60 * 5,
      initialData: serverCachedLandSaleCasesData,
    },
  );
  const { data: houseSaleCasesData } = useQuery(
    ['HousePrefectureSaleCases', prefectureCode],
    async () => {
      const result = await getSaleCasesList({
        prefecture_code: Number(prefectureCode),
        property_type: 'used_house,new_house',
        limit: 50,
      });
      return result.sale_cases || [];
    },
    {
      enabled: isValidUrl,
      cacheTime: 1000 * 60 * 5,
      initialData: serverCachedHouseSaleCasesData,
    },
  );

  const mapSaleCasesData = [...(landSaleCasesData || []), ...(houseSaleCasesData || [])];

  useEffect(() => {
    if (!isValidUrl) {
      router.replace('/404');
    }
  }, [isValidUrl]);

  //最初のタイトルの画面上端からのY座標を取得
  //※mobileでここまでスクロールしたらフローティングを表示する
  const [showFloatingThreshold, setShowFloatingThreshold] = useState(0);

  const showFloatingRef = useCallback(
    async (node: HTMLHeadingElement) => {
      if (isMobile) {
        //同階層ページ遷移時に諸々のレンダリング完了してから取得しないと正しい値が取得できない
        await new Promise((resolve) => setTimeout(resolve, 200));
        setShowFloatingThreshold(node?.getBoundingClientRect().top + document.documentElement.scrollTop - windowHeight);
      }
    },
    [windowHeight, prefectureCode],
  );

  const metaTitle = isValidUrl
    ? `【${displayPrefectureName}の土地・中古戸建て売却価格の相場と動向】をやさしく解説|実際の売却・査定のデータや事例を基に分析|不動産売却・買取専門のイクラ不動産`
    : '';

  const metaDescription = isValidUrl
    ? `「全国の土地・中古戸建て売却価格の相場と動向をやさしく解説」内の、「${displayPrefectureName}の土地・中古戸建て売却価格の相場と動向をやさしく解説」のページです。
    イクラ不動産が持つ実際の売却・査定のデータや事例を基に分析して、${displayPrefectureName}の土地・中古戸建ての平均売却価格や売却完了までの平均期間といった各種データから、
    高く売れる物件の特徴や相場全体の動向解説・事例、そして売却経験者へのアンケート結果まで幅広く掲載。
    ${displayPrefectureName}で土地・一戸建ての売却を検討する時に必要な情報をわかりやすく解説しています。`
    : '';

  const metaOGUrl = isValidUrl
    ? `${process.env.NEXT_PUBLIC_CLIENT_BASEPATH}/landhouses/prefectures/${prefectureCode}`
    : '';

  return (
    <>
      {isValidUrl && (
        <PublicPageContainer>
          <Head>
            <title key="title">{metaTitle}</title>
            <meta name="description" content={metaDescription} key="description" />
            <meta property="og:title" content={metaTitle} key={'og_title'} />
            <meta property="og:description" content={metaDescription} key={'og_description'} />
            <meta property="og:url" content={metaOGUrl} key={'og_url'} />
            <meta name="twitter:title" content={metaTitle} key={'twitter_title'} />
            <meta name="twitter:description" content={metaDescription} key={'twitter_description'} />
            <link rel="canonical" href={metaOGUrl} />
          </Head>
          <BreadCrumbs
            breadCrumbs={[
              { label: 'Home', url: '/' },
              { label: '全国の土地・中古戸建て売却価格の相場と動向', url: '/landhouses' },
              { label: `${displayPrefectureName}の土地・中古戸建て売却価格の相場と動向`, url: './' },
            ]}
          />

          <div className={styles.content}>
            <div className={styles.top_title}>
              {isValidUrl && (
                <SectionLead isH1Tag>
                  {displayPrefectureName}の土地・中古戸建て売却価格の相場と動向をやさしく解説
                </SectionLead>
              )}
            </div>

            <PageMainLead>
              {displayPrefectureName}
              の土地・中古戸建ての平均売却価格や売却完了までの平均期間といった各種データやアンケート等を幅広く掲載。
              <br className="pc_only" />
              {displayPrefectureName}で土地・一戸建ての売却を検討する時に必要な情報をわかりやすく解説しています。
            </PageMainLead>

            <div className={styles.general_info_container}>
              {landSalesStats && houseSalesStats && (
                <LandHouseGeneralInfo
                  pageType="prefecture"
                  areaInfo={{
                    country: '全国',
                    prefecture: displayPrefectureName,
                  }}
                  houseDisplayData={houseSalesStats}
                  landDisplayData={landSalesStats}
                />
              )}
            </div>

            <div className={`${styles.checkPrice_area_container} ${styles.stacks_container}`}>
              <div className={styles.first_card_container}>
                <LandHouseCard pageLabel={pageLabel} />
              </div>
              <SimulatorCard
                pageLabel={pageLabel}
                hasMansionLink={false}
                locationPropertyText={`${displayPrefectureName}の土地・中古戸建ての<br />`}
                prefectureCode={prefectureCode}
              />
            </div>

            <div className={styles.cta_container} ref={showFloatingRef}>
              <PrimaryCTA
                internalLink={{
                  path: `/companies/prefectures/${prefectureCode}`,
                  text: `【${displayPrefectureName}版】売却実績が豊富なおすすめの不動産屋さんランキングへ`,
                }}
              />
            </div>

            <div className={styles.area_link_container}>
              {cityGroups && landSalesStats && houseSalesStats && (
                <CitiesLinkList
                  pageType="landhouses"
                  landDisplayData={landSalesStats}
                  houseDisplayData={houseSalesStats}
                  prefectureId={Number(prefectureCode)}
                  citiesData={cityGroups}
                  location={'upper'}
                />
              )}
            </div>

            <div className={styles.internal_links_area}>
              <div className={styles.links_area_inner}>
                <div className={styles.internal_links_stacks}>
                  <Link
                    href={`/mansions/prefectures/${prefectureCode}`}
                    className={`${styles.internal_link_button} ${styles.internal_link_button_two_column}`}
                  >
                    {displayPrefectureName}のマンション売却相場を調べる
                  </Link>
                  <Link
                    href={`/companies/prefectures/${prefectureCode}`}
                    className={`${styles.internal_link_button} ${styles.internal_link_button_two_column}`}
                  >
                    {displayPrefectureName}のおすすめの不動産会社をみる
                  </Link>
                </div>
                <Link
                  href="/"
                  prefetch={false}
                  passHref
                  className={`${styles.internal_link_button} ${styles.internal_link_button_one_column}`}
                >
                  イクラ不動産トップへ
                </Link>
              </div>
            </div>

            <div className={styles.market_info_container}>
              {landSalesStats && (
                <PricePageMarketInfo
                  propertyType="land"
                  pageType="prefecture"
                  displayData={landSalesStats}
                  areaInfo={{ prefecture: displayPrefectureName }}
                />
              )}
            </div>

            {landSalesStats && landSalesStats.sales_stat_seller_surveys && (
              <div className={styles.sales_stats_container}>
                <SalesStatsSurveys
                  propertyType="land"
                  surveryType="seller"
                  SalesStatsSurveyData={landSalesStats.sales_stat_seller_surveys}
                  areaInfo={{ prefecture: displayPrefectureName }}
                />
              </div>
            )}

            {landSalesStats && landSalesStats.sales_stat_company_surveys && (
              <div className={styles.sales_stats_container}>
                <SalesStatsSurveys
                  propertyType="land"
                  surveryType="company"
                  SalesStatsSurveyData={landSalesStats.sales_stat_company_surveys}
                  areaInfo={{ prefecture: displayPrefectureName }}
                />
              </div>
            )}

            {landSalesStats && landSalesStats.sales_markets.urban_area.demand_description?.value && (
              <div className={styles.market_description_container}>
                <TrendDescription
                  propertyType="land"
                  displayData={landSalesStats}
                  areaInfo={{ prefecture: displayPrefectureName }}
                />
              </div>
            )}

            {landSaleCasesData && landSaleCasesData.length > 0 && (
              <>
                <div className={styles.saleCase_lead_container}>
                  <SectionLeadLeftAlign>【最新】{displayPrefectureName}の土地売却実績</SectionLeadLeftAlign>
                </div>

                <div className={styles.sale_case_area_container}>
                  <SaleCaseArea
                    saleCasesData={landSaleCasesData}
                    pageLabel={pageLabel}
                    showTableTitle={false}
                    onlyLandSaleCases={true}
                  />
                </div>
              </>
            )}
            <div className={styles.market_info_container}>
              {houseSalesStats && (
                <PricePageMarketInfo
                  propertyType="house"
                  pageType="prefecture"
                  displayData={houseSalesStats}
                  areaInfo={{ prefecture: displayPrefectureName }}
                />
              )}
            </div>

            {houseSalesStats && houseSalesStats.sales_stat_seller_surveys && (
              <div className={styles.sales_stats_container}>
                <SalesStatsSurveys
                  propertyType="house"
                  surveryType="seller"
                  SalesStatsSurveyData={houseSalesStats.sales_stat_seller_surveys}
                  areaInfo={{ prefecture: displayPrefectureName }}
                />
              </div>
            )}

            {houseSalesStats && houseSalesStats.sales_stat_company_surveys && (
              <div className={styles.sales_stats_container}>
                <SalesStatsSurveys
                  propertyType="house"
                  surveryType="company"
                  SalesStatsSurveyData={houseSalesStats.sales_stat_company_surveys}
                  areaInfo={{ prefecture: displayPrefectureName }}
                />
              </div>
            )}

            {houseSalesStats && houseSalesStats.sales_markets.urban_area.demand_description?.value && (
              <div className={styles.market_description_container}>
                <TrendDescription
                  propertyType="house"
                  displayData={houseSalesStats}
                  areaInfo={{ prefecture: displayPrefectureName }}
                />
              </div>
            )}

            {houseSaleCasesData && houseSaleCasesData.length > 0 && (
              <>
                <div className={styles.section_lead_left_container}>
                  <SectionLeadLeftAlign>【最新】{displayPrefectureName}の戸建て売却実績</SectionLeadLeftAlign>
                </div>
                <div className={styles.sale_case_area_container}>
                  <SaleCaseArea saleCasesData={houseSaleCasesData} pageLabel={pageLabel} showTableTitle={false} />
                </div>
              </>
            )}
            {mapSaleCasesData && (
              <div>
                <div className={styles.section_lead_left_container}>
                  <SectionLeadLeftAlign>
                    {displayPrefectureName}近辺で最近売れた土地・戸建を地図で確認する
                  </SectionLeadLeftAlign>
                </div>
                <div className={styles.sale_cases_map_container}>
                  <RenderOnScroll dependency={mapSaleCasesData}>
                    <SaleCasesMap
                      pageKey={`${pageLabel}${prefectureCode}`}
                      showCityName={true}
                      lat={prefectureLatLong.lat}
                      long={prefectureLatLong.lng}
                      zoom={zoomRate(prefectureCode)}
                      mapType={'price_page'}
                      saleCaseMapPinList={mapSaleCasesData}
                      fitBounds
                    />
                  </RenderOnScroll>
                </div>
              </div>
            )}

            <div className={styles.checkPrice_area_container}>
              <LandHouseCard pageLabel={pageLabel} />
            </div>

            <div className={styles.cta_container}>
              <PrimaryCTA
                internalLink={{
                  path: `/companies/prefectures/${prefectureCode}`,
                  text: `【${displayPrefectureName}版】売却実績が豊富なおすすめの不動産屋さんランキングへ`,
                }}
              />
            </div>

            {companiesData && companiesData.length > 0 && (
              <div className={styles.company_rank_section}>
                <div className={styles.section_lead_left_container}>
                  <SectionLeadLeftAlign>
                    【{displayPrefectureName}版】売却実績が豊富なおすすめの不動産屋TOP３ランキング
                  </SectionLeadLeftAlign>
                </div>

                <div className={styles.company_card_container}>
                  {companiesData.map((company, i) => {
                    return <CompanyCard rank={i + 1} key={i} data={company} pageQuery={'price'} />;
                  })}
                </div>
              </div>
            )}

            <div className={styles.ranking_container}>
              <WantToKnowRanking propertyType="land" />
            </div>

            <div className={styles.ranking_container}>
              <WantToKnowRanking propertyType="house" />
            </div>

            <div className={styles.cta_container}>
              <PrimaryCTA
                internalLink={{
                  path: `/companies/prefectures/${prefectureCode}`,
                  text: `【${displayPrefectureName}版】売却実績が豊富なおすすめの不動産屋さんランキングへ`,
                }}
              />
            </div>
            <div className={styles.checkPrice_area_container}>
              <SimulatorCard
                pageLabel={pageLabel}
                hasMansionLink={false}
                locationPropertyText={`${displayPrefectureName}の土地・中古戸建ての<br />`}
                prefectureCode={prefectureCode}
              />
            </div>
            <div className={styles.area_link_container}></div>

            <div className={styles.search_companies_stepper_container}>
              <SearchCompaniesWrapper hasShadow>
                <SearchCompaniesStepper pageLabel={pageLabel} defaultPrefecture={prefectureCode} location={'upper'} />
              </SearchCompaniesWrapper>
            </div>

            <div className={styles.area_link_container}>
              <div className={styles.section_title}>
                <SectionLeadLeftAlign>
                  都道府県別の、土地・中古戸建て売却価格査定額の相場・動向を調べる
                </SectionLeadLeftAlign>
              </div>
              <PrefecturesLinkList
                category="landhouse"
                houseRegionsAveragePrices={houseSalesStats?.regions_average_prices}
                landRegionsAveragePrices={landSalesStats?.regions_average_prices}
                location={'upper'}
              />
            </div>

            <div className={styles.internal_links_area}>
              <div className={styles.links_area_inner}>
                <div className={styles.internal_links_stacks}>
                  <Link
                    href={`/mansions/prefectures/${prefectureCode}`}
                    prefetch={false}
                    className={`${styles.internal_link_button} ${styles.internal_link_button_two_column}`}
                  >
                    {displayPrefectureName}のマンション売却相場を調べる
                  </Link>
                  <Link
                    prefetch={false}
                    href={`/companies/prefectures/${prefectureCode}`}
                    className={`${styles.internal_link_button} ${styles.internal_link_button_two_column}`}
                  >
                    {displayPrefectureName}のおすすめの不動産会社をみる
                  </Link>
                </div>
                <Link
                  href="/"
                  prefetch={false}
                  passHref
                  className={`${styles.internal_link_button} ${styles.internal_link_button_one_column}`}
                >
                  イクラ不動産トップへ
                </Link>
              </div>
            </div>
          </div>
          {/* end of .content  */}
          <Footer />
          <FloatingCV pageLabel={pageLabel} showFloatingThreshold={showFloatingThreshold} />
        </PublicPageContainer>
      )}
    </>
  );
};

export default LandHousesPrefecture;
